/**
 * @module config/cdnConfig
 * @name CDN Configuration
 * @description Zentrale CDN-Konfiguration für zur Laufzeit geladene Assets.
 * Bietet generische Funktionen für URL-Konstruktion, Fallback-Logik und optionale Health-Checks.
 * 
 * @example
 * import { createCdnConfig } from '@/config/cdnConfig'
 * 
 * const cdn = createCdnConfig({
 *   cdnBase: 'https://cdn.example.com/icons/',
 *   enableHealthCheck: true
 * })
 * 
 * // URL determiniert konstruieren
 * const url = cdn.getUrl('folder-1.svg') // -> https://cdn.example.com/icons/folder-1.svg oder Fallback
 * 
 * // Oder mit Fallback-Callback
 * const url = cdn.getUrl('folder-1.svg', () => 'data:image/svg+xml,...')
 */

/**
 * Cache für Health-Check-Ergebnisse
 * @type {Map<string, boolean>}
 */
const healthCheckCache = new Map();

/**
 * Erstellt ein CDN-Config-Objekt mit allen Hilfsfunktionen
 * @param {Object} config - Konfigurationsobjekt
 * @param {string} config.cdnBase - Basis-URL des CDN (z.B. 'https://cdn.example.com/icons/')
 * @param {boolean} [config.enableHealthCheck=false] - Health-Checks aktivieren
 * @param {number} [config.healthCheckTimeout=3000] - Timeout für Health-Checks in ms
 * @returns {Object} CDN-Config-Objekt mit Hilfsfunktionen
 */
function createCdnConfig(config = {}) {
  const {
    cdnBase = null,
    enableHealthCheck = false,
    healthCheckTimeout = 3000
  } = config;

  /**
   * Erstellt CDN-URL determiniert (ohne zu prüfen, ob sie erreichbar ist)
   * @param {string} filename - Dateiname (z.B. 'folder-1.svg')
   * @returns {string} Vollständige CDN-URL
   */
  function buildCdnUrl(filename) {
    if (!cdnBase) return null
    // Trailing slash sicherstellen
    const base = cdnBase.endsWith('/') ? cdnBase : cdnBase + '/';
    return base + filename
  }

  /**
   * Prüft ob CDN-URL erreichbar ist (mit Caching)
   * @param {string} url - URL zum Prüfen
   * @returns {Promise<boolean>}
   */
  async function checkUrlAvailable(url) {
    if (!enableHealthCheck) return true
    if (!url) return false
    
    // Cache prüfen
    if (healthCheckCache.has(url)) {
      return healthCheckCache.get(url)
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), healthCheckTimeout);

      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        credentials: 'include'
      });

      clearTimeout(timeout);
      const available = response.ok || response.status === 200;
      healthCheckCache.set(url, available);
      return available
    } catch (error) {
      healthCheckCache.set(url, false);
      return false
    }
  }

  /**
   * Gibt URL zurück: zuerst CDN, bei Fehler oder ohne Config Fallback
   * @param {string} filename - Dateiname
   * @param {string|Function} fallback - Fallback-URL (string) oder Callback-Function die URL zurückgibt
   * @returns {Promise<string>} URL zum Laden
   */
  async function getUrl(filename, fallback = null) {
    // Fallback auflösen (kann Function sein)
    const resolveFallback = () => {
      if (typeof fallback === 'function') return fallback()
      return fallback
    };

    // Wenn kein CDN konfiguriert, sofort Fallback returnen
    if (!cdnBase) {
      return resolveFallback()
    }

    const cdnUrl = buildCdnUrl(filename);

    // Health-Check durchführen wenn aktiviert
    if (enableHealthCheck) {
      const available = await checkUrlAvailable(cdnUrl);
      if (!available) {
        return resolveFallback()
      }
    }

    // Sonst CDN-URL zurückgeben (config-only fallback, kein Check nötig)
    return cdnUrl
  }

  /**
   * Synchrone Variante: CDN-URL determiniert oder Fallback (kein async)
   * Nutzen wenn man keine Async-Unterstützung braucht und auf Health-Check verzichtet
   * @param {string} filename
   * @param {string|Function} fallback
   * @returns {string}
   */
  function getUrlSync(filename, fallback = null) {
    const resolveFallback = () => {
      if (typeof fallback === 'function') return fallback()
      return fallback
    };

    if (!cdnBase) {
      return resolveFallback()
    }

    return buildCdnUrl(filename)
  }

  /**
   * Konfiguration zurückgeben
   */
  function getConfig() {
    return { cdnBase, enableHealthCheck, healthCheckTimeout }
  }

  return {
    buildCdnUrl,
    checkUrlAvailable,
    getUrl,
    getUrlSync,
    getConfig,
    isConfigured: () => !!cdnBase
  }
}

/**
 * Default-Export: Globale CDN-Instanz (wird von der App initialisiert)
 */
let globalCdnConfig = createCdnConfig();

function setGlobalCdnConfig(config) {
  globalCdnConfig = createCdnConfig(config);
}

function getGlobalCdnConfig() {
  return globalCdnConfig
}

export { createCdnConfig as c, getGlobalCdnConfig as g, setGlobalCdnConfig as s };
